import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBusinessMenuSnapshot } from "@/hooks/importers/useBusinessMenuSnapshot";
import {
  clearRun,
  isRunResolved,
  menuGrew,
  saveRun,
} from "@/lib/menuImportRuns";
import { Button } from "../";

export const formatRunTime = (iso) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));

const DOT = {
  progress: "bg-primary animate-pulse",
  success: "bg-[var(--success)]",
  danger: "bg-[var(--error)]",
  warning: "bg-[var(--warning)]",
};

const TEXT = {
  progress: "text-foreground",
  success: "text-success",
  danger: "text-error",
  warning: "text-foreground",
  neutral: "text-muted-foreground",
};

/**
 * Last-import line on an importer card.
 * Copy names what we know and never says "unknown".
 * Growth is reported as growth, never as completed.
 * A check that sees no growth closes the run as failed.
 */
const ImportRunStatus = ({ run, runRef, onRetry }) => {
  const { t } = useTranslation("menuImporters");
  const canCheck = run.businessIds?.length === 1;
  const { loading, failed, refetch } = useBusinessMenuSnapshot(
    canCheck ? run.businessIds[0] : null,
  );

  const counts = (menu) => t("run.menuCounts", menu);
  const unchangedMessage = t("run.unchanged");
  const checkedWithoutGrowth =
    run.status === "unknown" &&
    Boolean(run.checkedAt) &&
    Boolean(run.menuBefore) &&
    Boolean(run.menuAfter) &&
    !menuGrew(run.menuBefore, run.menuAfter);

  useEffect(() => {
    if (!checkedWithoutGrowth) return;
    saveRun(runRef, {
      ...run,
      status: "failed",
      finishedAt: run.checkedAt,
      errorMessage: unchangedMessage,
    });
  }, [checkedWithoutGrowth, run, runRef, unchangedMessage]);

  const handleCheck = async () => {
    const snapshot = await refetch();
    if (!snapshot) return;
    const checkedAt = new Date().toISOString();
    if (run.menuBefore && !menuGrew(run.menuBefore, snapshot)) {
      saveRun(runRef, {
        ...run,
        status: "failed",
        menuAfter: snapshot,
        checkedAt,
        finishedAt: checkedAt,
        errorMessage: unchangedMessage,
      });
      return;
    }
    saveRun(runRef, {
      ...run,
      menuAfter: snapshot,
      checkedAt,
    });
  };

  const handleDismiss = () => clearRun(runRef);

  const dismissButton = (
    <Button
      type="button"
      emphasis="link"
      intent="neutral"
      className="h-auto px-0 text-xs font-semibold"
      onClick={handleDismiss}
    >
      {t("run.dismiss")}
    </Button>
  );

  const checkButton = canCheck ? (
    <Button
      type="button"
      emphasis="link"
      intent="brand"
      className="h-auto px-0 text-xs font-semibold"
      onClick={handleCheck}
      disabled={loading}
    >
      {loading ? t("run.checking") : t("run.checkNow")}
    </Button>
  ) : null;

  const lostConnection = () => {
    if (isRunResolved(run)) {
      return {
        tone: "success",
        text: t("run.menuGrewResolved", {
          before: run.menuBefore.products,
          after: run.menuAfter.products,
          time: formatRunTime(run.checkedAt),
        }),
        actions: dismissButton,
      };
    }

    const checkedText = () => {
      if (!run.checkedAt || !run.menuAfter) {
        return t("run.connectionLost", { time: formatRunTime(run.startedAt) });
      }
      if (!run.menuBefore) {
        return t("run.stillRunningCounts", {
          counts: counts(run.menuAfter),
          time: formatRunTime(run.checkedAt),
        });
      }
      return t("run.connectionLost", { time: formatRunTime(run.startedAt) });
    };

    return {
      tone: "warning",
      text: checkedText(),
      detail: failed ? t("run.menuCheckFailed") : null,
      actions: (
        <>
          {checkButton}
          {dismissButton}
        </>
      ),
    };
  };

  const evidence = () => {
    if (run.businessIds?.length === 1 && run.menuAfter)
      return counts(run.menuAfter);
    const count = run.appliedCount ?? run.businessIds?.length ?? 0;
    return count ? t("run.storeCount", { count }) : null;
  };

  const byStatus = {
    importing: () => ({
      tone: "progress",
      text: t("run.importingSince", { time: formatRunTime(run.startedAt) }),
    }),
    success: () => ({
      tone: "success",
      text: [
        t("run.completedAt", { time: formatRunTime(run.finishedAt) }),
        evidence(),
      ]
        .filter(Boolean)
        .join(" · "),
      actions: dismissButton,
    }),
    failed: () => ({
      tone: "danger",
      text: t("run.failedAt", {
        time: formatRunTime(run.finishedAt),
        message: run.errorMessage || "",
      }),
      actions: (
        <>
          <Button
            type="button"
            emphasis="link"
            intent="brand"
            className="h-auto px-0 text-xs font-semibold"
            onClick={onRetry}
          >
            {t("actions.retry")}
          </Button>
          {dismissButton}
        </>
      ),
    }),
    unknown: lostConnection,
  };

  const view = checkedWithoutGrowth
    ? {
        tone: "danger",
        text: t("run.failedAt", {
          time: formatRunTime(run.checkedAt),
          message: unchangedMessage,
        }),
        actions: (
          <>
            <Button
              type="button"
              emphasis="link"
              intent="brand"
              className="h-auto px-0 text-xs font-semibold"
              onClick={onRetry}
            >
              {t("actions.retry")}
            </Button>
            {dismissButton}
          </>
        ),
      }
    : byStatus[run.status]?.();
  if (!view) return null;

  return (
    <div
      className="mt-2 flex flex-col gap-1"
      data-testid="importer-run-status"
      data-tone={view.tone}
    >
      <p
        className={`flex items-start gap-1.5 text-xs leading-snug ${TEXT[view.tone]}`}
      >
        <span
          className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${DOT[view.tone]}`}
          aria-hidden="true"
        />
        <span>{view.text}</span>
      </p>
      {view.detail && (
        <p className={`text-xs leading-snug ${TEXT.neutral}`}>{view.detail}</p>
      )}
      {view.actions && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 ps-3.5">
          {view.actions}
        </div>
      )}
    </div>
  );
};

export default ImportRunStatus;
